import { type Component, type JSX, For, Show, createEffect, createSignal, onCleanup } from 'solid-js';
import { ChevronDown } from 'lucide-solid';
import { cn } from '~/shared/lib/cn';
import { Popover } from './Popover';

export interface SelectOption<T extends string | number> {
  value: T;
  label: string;
  group?: string;
  disabled?: boolean;
}

interface SelectProps<T extends string | number> {
  value: T;
  onChange: (value: T) => void;
  options: Array<SelectOption<T>>;
  placeholder?: string;
  icon?: Component<{ class?: string }>;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  invalid?: boolean;
  align?: 'start' | 'center' | 'end';
  class?: string;
  ariaLabel?: string;
}


const SIZE_CLASS: Record<NonNullable<SelectProps<string>['size']>, string> = {
  sm: 'h-8 px-2.5 text-[12.5px]',
  md: 'h-10 px-3 text-[14px]',
  lg: 'h-11 px-3 text-[14px]',
};

export function Select<T extends string | number>(props: SelectProps<T>): JSX.Element {
  const [open, setOpen] = createSignal(false);
  const [anchor, setAnchor] = createSignal<HTMLElement>();
  const [minW, setMinW] = createSignal(0);
  let listRef: HTMLDivElement | undefined;

  const selected = () => props.options.find((o) => o.value === props.value);
  const display = () => selected()?.label ?? props.placeholder ?? '';

  const toggle = () => {
    if (props.disabled) return;
    const a = anchor();
    if (a) setMinW(a.getBoundingClientRect().width);
    setOpen((o) => !o);
  };

  createEffect(() => {
    if (!open()) return;
    const raf = requestAnimationFrame(() => {
      listRef?.querySelector('[data-selected="true"]')?.scrollIntoView({
        block: 'nearest',
        behavior: 'instant' as ScrollBehavior,
      });
    });
    onCleanup(() => cancelAnimationFrame(raf));
  });

  const grouped = () => {
    const order: string[] = [];
    const byGroup = new Map<string, Array<SelectOption<T>>>();
    const flat: Array<SelectOption<T>> = [];
    for (const o of props.options) {
      if (o.group) {
        if (!byGroup.has(o.group)) {
          byGroup.set(o.group, []);
          order.push(o.group);
        }
        byGroup.get(o.group)!.push(o);
      } else {
        flat.push(o);
      }
    }
    return { order, byGroup, flat };
  };

  const Icon = props.icon;

  const pick = (o: SelectOption<T>) => {
    if (o.disabled) return;
    props.onChange(o.value);
    setOpen(false);
  };

  const optionsOf = (list: HTMLElement): HTMLElement[] =>
    [...list.querySelectorAll<HTMLElement>('[data-select-option="true"]')].filter(
      (el) => !(el as HTMLButtonElement).disabled,
    );

  const onListKeyDown = (e: KeyboardEvent) => {
    const list = listRef;
    if (!list) return;
    const items = optionsOf(list);
    if (items.length === 0) return;

    const current = items.indexOf(document.activeElement as HTMLElement);
    const move = (next: number) => {
      e.preventDefault();
      items[Math.max(0, Math.min(items.length - 1, next))]?.focus();
    };

    switch (e.key) {
      case 'ArrowDown':
        move(current < 0 ? 0 : current + 1);
        break;
      case 'ArrowUp':
        move(current < 0 ? items.length - 1 : current - 1);
        break;
      case 'Home':
        move(0);
        break;
      case 'End':
        move(items.length - 1);
        break;
      default:
        break;
    }
  };

  const Option = (o: SelectOption<T>): JSX.Element => {
    const isSel = () => o.value === props.value;
    return (
      <button
        type="button"
        role="option"
        aria-selected={isSel()}
        data-select-option="true"
        data-selected={String(isSel())}
        disabled={o.disabled}
        class={cn(
          'press block w-full h-8 px-2.5 rounded-md text-[13px] tabular text-left truncate transition-colors',
          o.disabled
            ? 'text-disabled cursor-not-allowed'
            : isSel()
              ? 'text-white font-semibold'
              : 'text-primary hover:bg-white/8',
        )}
        style={isSel() && !o.disabled ? { background: 'var(--color-accent)' } : {}}
        onClick={() => pick(o)}
      >
        {o.label}
      </button>
    );
  };

  return (
    <>
      <button
        type="button"
        ref={setAnchor}
        disabled={props.disabled}
        aria-haspopup="listbox"
        aria-expanded={open()}
        aria-label={props.ariaLabel}
        onClick={toggle}
        class={cn(
          'press w-full rounded-md ring-hairline inline-flex items-center gap-2 text-left tabular transition-colors disabled:opacity-50',
          SIZE_CLASS[props.size ?? 'md'],
          props.class,
        )}
        style={{
          background: 'color-mix(in srgb, var(--color-bg-glass) 92%, transparent)',
          ...(open()
            ? { 'box-shadow': 'inset 0 0 0 1px var(--color-border-accent)' }
            : props.invalid
              ? { 'box-shadow': 'inset 0 0 0 1px var(--color-danger)' }
              : {}),
        }}
      >
        <Show when={Icon}>
          {(I) => {
            const Comp = I();
            return <Comp class="h-4 w-4 shrink-0 text-tertiary" />;
          }}
        </Show>
        <span class={cn('flex-1 truncate', selected() ? 'text-primary' : 'text-tertiary')}>
          {display()}
        </span>
        <ChevronDown
          class={cn(
            'h-4 w-4 shrink-0 text-tertiary transition-transform duration-fast',
            open() && 'rotate-180',
          )}
        />
      </button>

      <Popover open={open()} onClose={() => setOpen(false)} anchor={anchor()} align={props.align ?? 'start'}>
        <div
          ref={(el) => {
            listRef = el;
            queueMicrotask(() => {
              const items = optionsOf(el);
              (items.find((i) => i.dataset.selected === 'true') ?? items[0])?.focus();
            });
          }}
          role="listbox"
          tabindex={-1}
          onKeyDown={onListKeyDown}

          class="glass-menu p-1.5 animate-pop-in overflow-y-auto"
          style={{ width: `${minW() || 160}px`, 'max-height': '264px' }}
        >
          <For each={grouped().flat}>{(o) => Option(o)}</For>
          <For each={grouped().order}>
            {(g) => (
              <>
                <div class="px-2.5 pt-2 pb-1 text-[10px] uppercase tracking-[0.08em] text-tertiary">
                  {g}
                </div>
                <For each={grouped().byGroup.get(g)!}>{(o) => Option(o)}</For>
              </>
            )}
          </For>
        </div>
      </Popover>
    </>
  );
}
